/**
 * Watch: what is being monitored, and what has changed.
 *
 * TWO HALVES, AND THE FEED IS THE ONE THAT MATTERS. The list of subjects answers
 * "am I watching the right things"; the feed answers "what happened", which is
 * the question somebody actually opens this for. So the feed is the main column
 * and the subjects are a rail beside it, not a landing page you click through.
 *
 * Every arrangement decision lives in `pages/watchFeed.ts` — pure, tested, and
 * shared in spirit with the digest email's grouping so the two cannot come to
 * disagree about what "loudest" means.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import PageHeader from '../components/common/PageHeader';
import VerdictPanel from '../components/watch/VerdictPanel';
import NotificationPrefs from '../components/watch/NotificationPrefs';
import { OUTCOME_CLASSES, STATUS_CLASSES } from '../components/watch/tierTone';
import { groupByCheck, groupByDay, mergeAlerts, subjectStatus } from './watchFeed';
import type { WatchEvent, WatchFeedCounts, WatchSubject } from '../types';
import CheckCard from '../components/watch/CheckCard';

const PAGE = 50;

export default function WatchHome() {
  const [subjects, setSubjects] = useState<WatchSubject[]>([]);
  const [events, setEvents] = useState<WatchEvent[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [family, setFamily] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // THE CHIPS' NUMBERS COME FROM THE SERVER, a check at a time. Counted from
  // what was loaded, they counted alerts, and only the first 50 of them.
  const [counts, setCounts] = useState<WatchFeedCounts>({ families: [], unread: 0 });
  const { subscribe } = useWebSocket();

  const refreshCounts = useCallback(async () => {
    try {
      const page = await api.getWatchFeed({ limit: 1 });
      if (page.counts) setCounts(page.counts);
    } catch {
      /* stale chips are not worth an error banner */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subjectRows, page] = await Promise.all([
        api.getWatchSubjects(),
        api.getWatchFeed({ limit: PAGE, family: family ?? undefined, unreadOnly }),
      ]);
      setSubjects(subjectRows);
      setEvents(page.events);
      setNextBefore(page.next_before_id);
      if (page.counts) setCounts(page.counts);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load Watch');
    } finally {
      setLoading(false);
    }
  }, [family, unreadOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live alerts are folded in rather than triggering a reload: `mergeAlerts` is
  // idempotent on the id, so the socket and a later refetch cannot double them.
  // The first page under the current filters, so a filtered view gets only its own.
  useEffect(
    () =>
      subscribe((event) => {
        // A check that found nothing still moves the subject rail: its status line and its
        // "next check" both depend on a run having happened.
        if (event.event_type === 'watch_run' && event.status === 'finished') {
          void api
            .getWatchSubjects()
            .then(setSubjects)
            .catch(() => {
              /* a stale rail is not worth an error banner */
            });
          if (!event.events_written) return;
        }
        if (event.event_type !== 'watch_alert' && event.event_type !== 'watch_run') return;
        void (async () => {
          try {
            const page = await api.getWatchFeed({ limit: PAGE, family: family ?? undefined, unreadOnly });
            setEvents((current) => mergeAlerts(current, page.events));
            if (page.counts) setCounts(page.counts);
          } catch {
            /* a stale feed is not worth an error banner; the next load fixes it */
          }
        })();
      }),
    [subscribe, family, unreadOnly],
  );

  const days = useMemo(() => groupByDay(events), [events]);
  const families = counts.families;
  const unread = counts.unread;

  const markAllRead = async () => {
    const newest = events[0]?.detected_at;
    if (!newest) return;
    // `before` rather than a blanket update, so an event that arrives between the render and the
    // click is not swallowed.
    await api.markWatchSeen({ before: newest });
    setEvents((current) => current.map((e) => ({ ...e, seen_at: e.seen_at ?? newest })));
    void refreshCounts();
  };

  // A check's card dismisses every alert it raised.
  const dismissMany = async (ids: number[]) => {
    await Promise.all(ids.map((id) => api.dismissWatchEvent(id)));
    setEvents((current) => current.filter((e) => !ids.includes(e.id)));
    void refreshCounts();
  };

  const loadMore = async () => {
    if (nextBefore == null) return;
    const page = await api.getWatchFeed({
      limit: PAGE,
      beforeId: nextBefore,
      family: family ?? undefined,
      unreadOnly,
    });
    setEvents((current) => [...current, ...page.events]);
    setNextBefore(page.next_before_id);
  };

  return (
    <div>
      {/* No breadcrumb: this is a list root, which PageHeader says is already the top. Width and
          padding belong to `Layout`, so this page adds neither. */}
      <PageHeader
        title="Watch"
        subtitle="What your competitors and markets are doing, as it changes."
        className="mb-4"
        actions={
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setUnreadOnly((v) => !v)}
              className={`px-2 py-1 rounded border ${
                unreadOnly
                  ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              Unread{unread ? ` (${unread})` : ''}
            </button>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
              >
                Mark all read
              </button>
            )}
          </div>
        }
      />

      {error && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      {/* ACCOUNT-WIDE, and this is the only screen where that reading makes sense: standing on one
          watch you can see it is noisy, but not that it is most of what you hear. */}
      <VerdictPanel title="How your watches are working" />

      {/* Account-wide, so it belongs beside the list rather than on a subject: a tier floor and a
          digest cadence are decisions about your inbox, not about one competitor. */}
      <NotificationPrefs />

      <div className="grid gap-6 md:grid-cols-[1fr_260px]">
        <section>
          {(families.length > 1 || family !== null) && (
            <div className="mb-3 flex flex-wrap gap-1 text-[11px]">
              <FamilyChip active={family === null} onClick={() => setFamily(null)} label="All" />
              {families.map((f) => (
                <FamilyChip
                  key={f.family}
                  active={family === f.family}
                  onClick={() => setFamily(f.family)}
                  label={`${f.title} (${f.count})`}
                />
              ))}
            </div>
          )}

          {loading && <p className="text-sm text-gray-500">Loading…</p>}

          {!loading && !events.length && (
            <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
              <p className="text-sm font-medium">Nothing has changed yet.</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {subjects.length
                  ? 'Your watches are running. The first look at something records what is there now and reports nothing - changes come after that.'
                  : 'Add something to watch, or press “Watch this company” on a competitor in a recon.'}
              </p>
            </div>
          )}

          {days.map((day) => (
            <div key={day.day} className="mb-6">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {day.label}
              </h2>
              {/* ONE CARD PER CHECK, not per alert (see `groupByCheck`). */}
              <ul className="space-y-2">
                {groupByCheck(day.events).map((check) => (
                  <CheckCard
                    key={check.key}
                    runId={check.runId}
                    events={check.events}
                    onDismiss={(ids) => void dismissMany(ids)}
                  />
                ))}
              </ul>
            </div>
          ))}

          {nextBefore != null && (
            <button
              type="button"
              onClick={loadMore}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
            >
              Show older
            </button>
          )}
        </section>

        <aside>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Watching
            </h2>
            <Link to="/watch/new" className="text-[11px] underline">
              Add
            </Link>
          </div>
          <ul className="space-y-1">
            {subjects.map((subject) => (
              <SubjectRow key={subject.id} subject={subject} />
            ))}
            {!subjects.length && !loading && (
              <li className="text-[11px] text-gray-500 dark:text-gray-400">
                Nothing yet.
              </li>
            )}
          </ul>
        </aside>
      </div>
    </div>
  );
}

function FamilyChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-0.5 rounded-full border ${
        active
          ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900'
          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
      }`}
    >
      {label}
    </button>
  );
}

function SubjectRow({ subject }: { subject: WatchSubject }) {
  const status = subjectStatus(subject);
  return (
    <li>
      <Link
        to={`/watch/subjects/${subject.id}`}
        className="block rounded px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <div className="flex items-center gap-2">
          <span
            className={`h-1.5 w-1.5 rounded-full shrink-0 ${STATUS_CLASSES[status.tone]}`}
            aria-hidden
          />
          <span className="text-sm truncate">{subject.name}</span>
          {subject.unread > 0 && (
            <span className="ml-auto text-[10px] px-1.5 rounded-full bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
              {subject.unread}
            </span>
          )}
        </div>
        <p className={`pl-3.5 text-[11px] ${OUTCOME_CLASSES[status.tone === 'problem' ? 'bad' : 'quiet']}`}>
          {status.text}
        </p>
      </Link>
    </li>
  );
}
